"use client";

import { useState } from "react";
import { Product, PRODUCT_TYPES, PRODUCT_PRICE_TYPES, type AppLocale, type FunnelStageConfig, type RegionalSettings } from "@/lib/funnel-planner/schema";
import type { FunnelPlannerContent } from "@/lib/content/entries/funnel-planner";
import { formatFunnelCurrency, funnelText, guaranteeLabel, priceTypeLabel, productTypeLabel } from "@/lib/funnel-planner/i18n";
import { CustomSelect } from "./CustomSelect";

type ProductCardProps = {
  product: Product;
  onChange: (updated: Product) => void;
  onRemove: () => void;
  index: number;
  expanded?: boolean;
  onExpandToggle?: () => void;
  inputTabIndex?: number;
  content: FunnelPlannerContent;
  locale: AppLocale;
  regionalSettings: RegionalSettings;
  stages: FunnelStageConfig[];
};

const PRODUCT_TYPE_LABELS: Record<string, string> = {
  online_course: "Online Course",
  saas: "SaaS",
  membership: "Membership",
  digital_download: "Digital Download",
  coaching: "Coaching",
  done_for_you: "Done-For-You",
  agency_retainer: "Agency Retainer",
  physical_product: "Physical Product",
  supplement: "Supplement",
  affiliate: "Affiliate",
  high_ticket: "High Ticket",
  low_ticket: "Low Ticket",
  free_plus_shipping: "Free + Shipping",
  event: "Event",
  paid_newsletter: "Paid Newsletter",
  paid_community: "Paid Community",
  software_plugin: "Software Plugin",
  mentorship: "Mentorship",
  subscription_box: "Subscription Box",
  usage_based_api: "Usage-Based API",
  seat_based_saas: "Seat-Based SaaS",
  mobile_app: "Mobile App",
  cohort_course: "Cohort Course",
  franchise: "Franchise",
  local_service: "Local Service",
  ai_agent: "AI Agent",
  other: "Other",
};

const PRICE_TYPE_LABELS: Record<string, string> = {
  one_time: "One-Time",
  monthly: "Monthly",
  yearly: "Yearly",
  usage_based: "Usage-Based",
  seat_based: "Seat-Based",
  payment_plan: "Payment Plan",
};

const GUARANTEE_LABELS: Record<string, string> = {
  none: "No Guarantee",
  money_back_30: "30-Day Money Back",
  money_back_60: "60-Day Money Back",
  satisfaction: "Satisfaction Guarantee",
  results: "Results Guarantee",
};

const OFFER_ROLE_OPTIONS = [
  { value: "core", key: "productOfferCoreOption", fallback: "Core offer" },
  { value: "order_bump", key: "productOfferOrderBumpOption", fallback: "Order bump" },
  { value: "upsell", key: "productOfferUpsellOption", fallback: "Upsell" },
  { value: "downsell", key: "productOfferDownsellOption", fallback: "Downsell" },
  { value: "backend", key: "productOfferBackendOption", fallback: "Backend offer" },
  { value: "continuity", key: "productOfferContinuityOption", fallback: "Continuity" },
  { value: "application_only", key: "productOfferApplicationOnlyOption", fallback: "Application only" },
  { value: "quote_only", key: "productOfferQuoteOnlyOption", fallback: "Quote only" },
] as const;

const CHECKOUT_TYPE_OPTIONS = [
  { value: "one_step", key: "productCheckoutOneStepOption", fallback: "One-step checkout" },
  { value: "two_step", key: "productCheckoutTwoStepOption", fallback: "Two-step checkout" },
  { value: "invoice", key: "productCheckoutInvoiceOption", fallback: "Invoice" },
  { value: "subscription_checkout", key: "productCheckoutSubscriptionOption", fallback: "Subscription checkout" },
  { value: "sales_call_close", key: "productCheckoutSalesCallOption", fallback: "Sales call close" },
] as const;

const FULFILLMENT_COST_TYPE_OPTIONS = [
  { value: "none", key: "productFulfillmentNoneOption", fallback: "None" },
  { value: "one_time", key: "productFulfillmentOneTimeOption", fallback: "One time" },
  { value: "recurring", key: "productFulfillmentRecurringOption", fallback: "Recurring" },
  { value: "per_order", key: "productFulfillmentPerOrderOption", fallback: "Per order" },
] as const;

function currencyPrefix(locale: AppLocale, regionalSettings: RegionalSettings): string {
  const formatted = formatFunnelCurrency(0, locale, regionalSettings);
  return formatted.replace(/[\d\s.,\u00a0]/g, "") || regionalSettings.currency;
}

const isRecurring = (priceType: string) =>
  priceType === "monthly" || priceType === "yearly";

const LABEL_S: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: "#94a3b8",
  display: "block",
  marginBottom: 4,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
};

const INPUT_S: React.CSSProperties = {
  width: "100%",
  borderRadius: 6,
  border: "1px solid #334155",
  background: "rgba(255,255,255,0.05)",
  padding: "7px 10px",
  fontSize: 13,
  color: "#e2e8f0",
  outline: "2px solid transparent",
  boxSizing: "border-box",
};


function NumericInput({
  label,
  value,
  onChange,
  min,
  max,
  step,
  prefix,
  suffix,
  inputId,
  onDirty,
  content,
}: {
  label: string;
  value: number | undefined;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  prefix?: string;
  suffix?: string;
  inputId: string;
  onDirty?: () => void;
  content: FunnelPlannerContent;
}) {
  const [localValue, setLocalValue] = useState<string>(value != null ? String(value) : "");
  const [error, setError] = useState<string | null>(null);
  const [focused, setFocused] = useState(false);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setLocalValue(e.target.value);
    setError(null);
    if (onDirty) onDirty();
  }

  function handleBlur() {
    setFocused(false);
    const trimmed = localValue.trim();
    if (trimmed === "") {
      setError(funnelText(content, "validationRequired", "Required"));
      return;
    }
    const parsed = parseFloat(trimmed);
    if (isNaN(parsed)) {
      setError(funnelText(content, "validationEnterNumber", "Enter a number"));
      return;
    }
    if (min != null && parsed < min) {
      setError(funnelText(content, "validationMinimum", "Minimum is {min}", { min }));
      return;
    }
    if (max != null && parsed > max) {
      setError(funnelText(content, "validationMaximum", "Maximum is {max}", { max }));
      return;
    }
    setError(null);
    setLocalValue(String(parsed));
    onChange(parsed);
  }

  const wrapperBorder = error
    ? "1px solid #ef4444"
    : focused
    ? "1px solid #2563eb"
    : "1px solid #334155";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label htmlFor={inputId} style={LABEL_S}>{label}</label>
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 4,
        borderRadius: 6,
        border: wrapperBorder,
        background: "rgba(255,255,255,0.05)",
        padding: "7px 10px",
        boxShadow: focused ? "0 0 0 2px rgba(37,99,235,0.25)" : "none",
        transition: "border-color 0.15s, box-shadow 0.15s",
      }}>
        {prefix && <span style={{ fontSize: 12, color: "#94a3b8", flexShrink: 0 }}>{prefix}</span>}
        <input
          id={inputId}
          type="number"
          style={{
            flex: 1,
            minWidth: 0,
            background: "transparent",
            border: "none",
            outline: "2px solid transparent",
            fontSize: 13,
            color: "#e2e8f0",
          }}
          value={localValue}
          min={min}
          max={max}
          step={step ?? 1}
          onChange={handleChange}
          onFocus={() => { setFocused(true); setLocalValue(value != null ? String(value) : ""); }}
          onBlur={handleBlur}
        />
        {suffix && <span style={{ fontSize: 12, color: "#94a3b8", flexShrink: 0 }}>{suffix}</span>}
      </div>
      {error && (
        <span role="alert" style={{ fontSize: 11, color: "#ef4444", marginTop: 2 }}>{error}</span>
      )}
    </div>
  );
}

export function ProductCard({ product, onChange, onRemove, index, content, locale, regionalSettings, stages }: ProductCardProps) {
  const moneyPrefix = currencyPrefix(locale, regionalSettings);
  const recurring = isRecurring(product.priceType);
  const fieldId = (name: string) => `product-${index}-${name}`;
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [advHovered, setAdvHovered] = useState(false);

  function update(patch: Partial<Product>) {
    onChange({ ...product, ...patch });
  }

  const priceSuffix =
    product.priceType === "monthly" ? "/mo"
    : product.priceType === "yearly" ? "/yr"
    : product.priceType === "seat_based" ? "/seat/mo"
    : undefined;

  return (
    <div style={{
      borderRadius: 12,
      border: "1px solid #334155",
      background: "rgba(255,255,255,0.04)",
      padding: 16,
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <label htmlFor={fieldId("name")} style={{ ...LABEL_S, marginBottom: 4 }}>
            {funnelText(content, "productNameLabel", "Product {index} Name", { index: index + 1 })}
          </label>
          <input
            id={fieldId("name")}
            style={{ ...INPUT_S, fontWeight: 600, fontSize: 14, color: "#f1f5f9" }}
            value={product.name}
            maxLength={80}
            onChange={(e) => update({ name: e.target.value })}
            onFocus={(e) => { (e.currentTarget as HTMLInputElement).style.outline = "2px solid #3b82f6"; (e.currentTarget as HTMLInputElement).style.outlineOffset = "1px"; }}
            onBlur={(e) => { (e.currentTarget as HTMLInputElement).style.outline = "2px solid transparent"; }}
            placeholder={funnelText(content, "productNamePlaceholder", "e.g. Core Offer, Order Bump, Upsell 1")}
          />
        </div>
        <button
          type="button"
          onClick={onRemove}
          aria-label={funnelText(content, "productRemoveAria", "Remove product")}
          style={{
            marginTop: 20,
            borderRadius: 6,
            padding: 14,
            minWidth: 44,
            minHeight: 44,
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: "#64748b",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            outline: "2px solid transparent",
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = "#ef4444"; }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = "#64748b"; }}
          onFocus={e => { (e.currentTarget as HTMLButtonElement).style.boxShadow = "0 0 0 2px #ef4444"; }}
          onBlur={e => { (e.currentTarget as HTMLButtonElement).style.boxShadow = "none"; }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={LABEL_S}>{funnelText(content, "productTypeLabel", "Product Type")}</label>
        <CustomSelect
          value={product.productType}
          onChange={(value) => update({ productType: value as Product["productType"] })}
          options={PRODUCT_TYPES.map((t) => ({ value: t, label: productTypeLabel(content, t, PRODUCT_TYPE_LABELS[t] ?? t) }))}
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        <div>
          <label style={LABEL_S}>{funnelText(content, "productPriceTypeLabel", "Price Type")}</label>
          <CustomSelect
            value={product.priceType}
            onChange={(v) => update({ priceType: v as Product["priceType"] })}
            options={PRODUCT_PRICE_TYPES.map((t) => ({ value: t, label: priceTypeLabel(content, t, PRICE_TYPE_LABELS[t] ?? t) }))}
          />
        </div>
        <NumericInput
          inputId={fieldId("price")}
          label={priceSuffix
            ? funnelText(content, "productPriceWithSuffixLabel", "Price ({suffix})", { suffix: priceSuffix })
            : funnelText(content, "productPriceLabel", "Price")}
          value={product.price}
          onChange={(v) => update({ price: v })}
          min={0}
          step={1}
          prefix={moneyPrefix}
          content={content}
        />
      </div>

      {recurring && (
        <div style={{ marginBottom: 12 }}>
          <div style={{
            fontSize: 11,
            fontWeight: 600,
            color: "#3b82f6",
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            marginBottom: 8,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}>
            <span style={{
              display: "inline-block",
              padding: "2px 8px",
              borderRadius: 4,
              background: "rgba(37,99,235,0.15)",
              border: "1px solid rgba(37,99,235,0.4)",
            }}>
              {funnelText(content, "productRecurringPricing", "Recurring Pricing")}
            </span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <NumericInput
              inputId={fieldId("monthly-churn")}
              label={funnelText(content, "productMonthlyChurnLabel", "Monthly Churn %")}
              value={product.churnRatePercent ?? 5}
              onChange={(v) => update({ churnRatePercent: v })}
              min={0} max={100} step={0.5} suffix="%"
              content={content}
            />
            <NumericInput
              inputId={fieldId("months-retained")}
              label={funnelText(content, "productMonthsRetainedLabel", "Avg Months Retained")}
              value={product.monthsRetained ?? 12}
              onChange={(v) => update({ monthsRetained: v })}
              min={1} max={120}
              content={content}
            />
          </div>
          <div style={{
            borderRadius: 8,
            border: "1px solid #334155",
            background: "rgba(255,255,255,0.03)",
            padding: 12,
            marginBottom: 0,
          }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, fontWeight: 500, color: "#cbd5e1" }}>
              <span style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, flexShrink: 0 }}>
                <input
                  type="checkbox"
                  checked={product.hasTrial}
                  onChange={(e) => {
                    if (e.target.checked) {
                      update({ hasTrial: true });
                    } else {
                      update({ hasTrial: false, trialDays: undefined, trialToPaidCvrPct: undefined });
                    }
                  }}
                  style={{ accentColor: "#3b82f6", width: 16, height: 16, cursor: "pointer" }}
                />
              </span>
              {funnelText(content, "productFreeTrialLabel", "Offer a free trial")}
            </label>
            {product.hasTrial && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
                <NumericInput
                  inputId={fieldId("trial-days")}
                  label={funnelText(content, "productTrialDaysLabel", "Trial Days")}
                  value={product.trialDays ?? 14}
                  onChange={(v) => update({ trialDays: v })}
                  min={1} max={365}
                  content={content}
                />
                <NumericInput
                  inputId={fieldId("trial-to-paid-cvr")}
                  label={funnelText(content, "productTrialToPaidLabel", "Trial-to-Paid CVR %")}
                  value={product.trialToPaidCvrPct ?? 30}
                  onChange={(v) => update({ trialToPaidCvrPct: v })}
                  min={0} max={100} step={0.5} suffix="%"
                  content={content}
                />
              </div>
            )}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => setShowAdvanced((s) => !s)}
        aria-expanded={showAdvanced}
        aria-controls={fieldId("advanced-section")}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: advHovered ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.03)",
          border: "1px solid #334155",
          borderRadius: 6,
          padding: "8px 12px",
          cursor: "pointer",
          color: "#94a3b8",
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          marginBottom: showAdvanced ? 12 : 0,
          outline: "2px solid transparent",
          transition: "background 0.15s",
          minHeight: 44,
        }}
        onMouseEnter={() => setAdvHovered(true)}
        onMouseLeave={() => setAdvHovered(false)}
        onFocus={(e) => { (e.currentTarget as HTMLButtonElement).style.outline = "2px solid #3b82f6"; }}
        onBlur={(e) => { (e.currentTarget as HTMLButtonElement).style.outline = "2px solid transparent"; }}
      >
        <span>{funnelText(content, "productAdvancedLabel", "Advanced")}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 20 20"
          fill="currentColor"
          style={{ transform: showAdvanced ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}
        >
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>

      {showAdvanced && (
        <div id={fieldId("advanced-section")}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
            <NumericInput
              inputId={fieldId("refund-rate")}
              label={funnelText(content, "productRefundRateLabel", "Refund Rate %")}
              value={product.refundRatePercent}
              onChange={(v) => update({ refundRatePercent: v })}
              min={0} max={100} step={0.5} suffix="%"
              content={content}
            />
            <NumericInput
              inputId={fieldId("cogs")}
              label={funnelText(content, "productCogsLabel", "Production Costs %")}
              value={product.cogsPercent}
              onChange={(v) => update({ cogsPercent: v })}
              min={0} max={100} step={0.5} suffix="%"
              content={content}
            />
            <NumericInput
              inputId={fieldId("merchant-fee")}
              label={funnelText(content, "productMerchantFeeLabel", "Merchant Fee %")}
              value={product.merchantFeePercent}
              onChange={(v) => update({ merchantFeePercent: v })}
              min={0} max={10} step={0.1} suffix="%"
              content={content}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={LABEL_S}>{funnelText(content, "productGuaranteeLabel", "Guarantee")}</label>
            <CustomSelect
              value={product.guaranteeType}
              onChange={(v) => update({ guaranteeType: v as Product["guaranteeType"] })}
              options={(["none", "money_back_30", "money_back_60", "satisfaction", "results"] as const).map((g) => ({
                value: g,
                label: guaranteeLabel(content, g, GUARANTEE_LABELS[g]),
              }))}
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={LABEL_S}>{funnelText(content, "productOfferRoleLabel", "Offer role")}</label>
              <CustomSelect
                value={product.offerRole ?? (product.isUpsell ? "upsell" : "core")}
                onChange={(v) => update({
                  offerRole: v as Product["offerRole"],
                  isUpsell: v === "upsell" || v === "order_bump" || v === "downsell",
                })}
                options={OFFER_ROLE_OPTIONS.map((option) => ({
                  value: option.value,
                  label: funnelText(content, option.key, option.fallback),
                }))}
              />
            </div>
            <div>
              <label style={LABEL_S}>{funnelText(content, "productCheckoutTypeLabel", "Checkout type")}</label>
              <CustomSelect
                value={product.checkoutType ?? "one_step"}
                onChange={(v) => update({ checkoutType: v as Product["checkoutType"] })}
                options={CHECKOUT_TYPE_OPTIONS.map((option) => ({
                  value: option.value,
                  label: funnelText(content, option.key, option.fallback),
                }))}
              />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={LABEL_S}>{funnelText(content, "productAttachedStageLabel", "Attached stage")}</label>
              <CustomSelect
                value={product.attachedStageIds?.[0] ?? ""}
                onChange={(v) => update({ attachedStageIds: v ? [v] : [] })}
                options={[
                  { value: "", label: funnelText(content, "productNoAttachedStageOption", "No specific stage") },
                  ...stages.map((stage) => ({ value: stage.id, label: stage.name })),
                ]}
              />
            </div>
            <NumericInput
              inputId={fieldId("sales-split")}
              label={funnelText(content, "productSalesSplitLabel", "Sales split %")}
              value={product.productSalesSplitPct ?? 100}
              onChange={(v) => update({ productSalesSplitPct: v })}
              min={0} max={100} step={1} suffix="%"
              content={content}
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
            <NumericInput
              inputId={fieldId("order-bump-take-rate")}
              label={funnelText(content, "productOrderBumpTakeRateLabel", "Order bump take rate %")}
              value={product.orderBumpTakeRatePct ?? 20}
              onChange={(v) => update({ orderBumpTakeRatePct: v })}
              min={0} max={100} step={0.5} suffix="%"
              content={content}
            />
            <NumericInput
              inputId={fieldId("downsell-take-rate")}
              label={funnelText(content, "productDownsellTakeRateLabel", "Downsell take rate %")}
              value={product.downsellTakeRatePct ?? 12}
              onChange={(v) => update({ downsellTakeRatePct: v })}
              min={0} max={100} step={0.5} suffix="%"
              content={content}
            />
            <NumericInput
              inputId={fieldId("merchant-fixed-fee")}
              label={funnelText(content, "productMerchantFixedFeeLabel", "Processor fixed fee")}
              value={product.merchantFixedFee ?? 0.3}
              onChange={(v) => update({ merchantFixedFee: v })}
              min={0} max={100} step={0.01} prefix={moneyPrefix}
              content={content}
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={LABEL_S}>{funnelText(content, "productFulfillmentCostTypeLabel", "Fulfillment type")}</label>
              <CustomSelect
                value={product.fulfillmentCostType ?? "none"}
                onChange={(v) => update({ fulfillmentCostType: v as Product["fulfillmentCostType"] })}
                options={FULFILLMENT_COST_TYPE_OPTIONS.map((option) => ({
                  value: option.value,
                  label: funnelText(content, option.key, option.fallback),
                }))}
              />
            </div>
            <NumericInput
              inputId={fieldId("fulfillment-cost")}
              label={funnelText(content, "productFulfillmentCostLabel", "Fulfillment cost")}
              value={product.fulfillmentCostAmount ?? 0}
              onChange={(v) => update({ fulfillmentCostAmount: v })}
              min={0} step={1} prefix="$"
              content={content}
            />
          </div>

          <div style={{
            borderRadius: 8,
            border: "1px solid #334155",
            background: "rgba(255,255,255,0.03)",
            padding: 12,
          }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, fontWeight: 500, color: "#cbd5e1" }}>
              <span style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 24, height: 24, flexShrink: 0 }}>
                <input
                  type="checkbox"
                  checked={product.isUpsell}
                  onChange={(e) => {
                    if (e.target.checked) {
                      update({ isUpsell: true });
                    } else {
                      update({ isUpsell: false, upsellTakeRatePct: undefined });
                    }
                  }}
                  style={{ accentColor: "#3b82f6", width: 16, height: 16, cursor: "pointer" }}
                />
              </span>
              {funnelText(content, "productUpsellLabel", "This is an upsell / order bump")}
            </label>
            {product.isUpsell && (
              <div style={{ marginTop: 12 }}>
                <NumericInput
                  inputId={fieldId("upsell-take-rate")}
                  label={funnelText(content, "productUpsellTakeRateLabel", "Upsell Take Rate %")}
                  value={product.upsellTakeRatePct ?? 25}
                  onChange={(v) => update({ upsellTakeRatePct: v })}
                  min={0} max={100} step={0.5} suffix="%"
                  content={content}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
